import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Loader,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  ContactInfoPageValidation,
  Customer,
  CustomerContactInfo,
  CustomerOperations,
} from 'src/front/api/apiInstance';
import {
  CardSelectionType,
  CustomerContactInfoFormatResponse,
  PhoneType,
  ReferenceDataType,
  SearchBankruptInterfaceType,
} from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import {
  dictionaryLineFormat,
  formatEmail,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { PhoneEmailSchema } from 'src/front/common/utils/validationSchemas';

import {
  Container,
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { BREAK_POINTS } from 'src/front/constant';
import { useSearchBankrupt } from 'src/front/contexts/BankruptContext';
import { useCardTypeState, useSwitchCustomerType } from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

export const FlexFormElement = styled(FormElement)`
  display: flex;
  justify-content: space-between;
`;

export const AreaCode = styled.div`
  width: 39%;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    width: 36%;
  }
`;

export const PhoneNumber = styled.div`
  width: 59%;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    width: 62%;
  }
`;

export interface PhoneValidations {
  mobilePhone?: CustomerContactInfoFormatResponse;
  actualLivingAddressPhone?: CustomerContactInfoFormatResponse;
  workPhone?: CustomerContactInfoFormatResponse;
  contactPersonMobilePhone1?: CustomerContactInfoFormatResponse;
  contactPersonMobilePhone2?: CustomerContactInfoFormatResponse;
}

interface FormData {
  emailAddress: string;
  mobilePhoneCountry: Option | null;
  mobilePhoneFieldCode: string;
  mobilePhoneTelephoneNo: string;
  actualLivingAddressPhoneCountry: Option | null;
  actualLivingAddressPhoneFieldCode: string;
  actualLivingAddressPhoneTelephoneNo: string;
  workPhoneCountry: Option | null;
  workPhoneFieldCode: string;
  workPhoneTelephoneNo: string;
  contactPersonMobilePhone1Country: Option | null;
  contactPersonMobilePhone1FieldCode: string;
  contactPersonMobilePhone1TelephoneNo: string;
  contactPersonMobilePhone2Country: Option | null;
  contactPersonMobilePhone2FieldCode: string;
  contactPersonMobilePhone2TelephoneNo: string;
}

const defaultValues = {
  emailAddress: '',
  mobilePhoneCountry: null,
  mobilePhoneFieldCode: '',
  mobilePhoneTelephoneNo: '',
  actualLivingAddressPhoneCountry: null,
  actualLivingAddressPhoneFieldCode: '',
  actualLivingAddressPhoneTelephoneNo: '',
  workPhoneCountry: null,
  workPhoneFieldCode: '',
  workPhoneTelephoneNo: '',
  contactPersonMobilePhone1Country: null,
  contactPersonMobilePhone1FieldCode: '',
  contactPersonMobilePhone1TelephoneNo: '',
  contactPersonMobilePhone2Country: null,
  contactPersonMobilePhone2FieldCode: '',
  contactPersonMobilePhone2TelephoneNo: '',
};

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

const PhoneEmailEntry: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();
  const { searchBankrupt } = useSearchBankrupt();

  const switchCustomerTypeDispatch = useSwitchCustomerType();

  const isCreditCard = useCardTypeState() === CardSelectionType.CREDITCARD;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [formValidations, setFormValidations] = useState<PhoneValidations>();
  const countryPhoneCodes = usePRM(ReferenceDataType.COUNTRYCODETELEPHONE);
  const isNewCustomer = !customerId;

  const { handleSubmit, errors, setValue, control, watch, formState } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(PhoneEmailSchema, {
      abortEarly: false,
      context: { formValidations, isCreditCard },
    }),
  });

  const loadPhoneEmailInformation = useCallback(async () => {
    const { data } = await ContactInfoPageValidation.getContactInfoPageData();
    if (data.result === 'OK' && data.pageData) {
      const {
        emailAddress,
        mobilePhone,
        actualLivingAddressPhone,
        workPhone,
        contactPersonMobilePhone1,
        contactPersonMobilePhone2,
      } = data.pageData;
      [
        { name: 'emailAddress', value: emailAddress },
        { name: 'mobilePhoneCountry', value: optionFormat(mobilePhone.countryCode) },
        { name: 'mobilePhoneFieldCode', value: mobilePhone.fieldCode },
        { name: 'mobilePhoneTelephoneNo', value: mobilePhone.telephoneNo },
        ...(actualLivingAddressPhone
          ? [
              {
                name: 'actualLivingAddressPhoneCountry',
                value: optionFormat(actualLivingAddressPhone.countryCode),
              },
              {
                name: 'actualLivingAddressPhoneFieldCode',
                value: actualLivingAddressPhone.fieldCode,
              },
              {
                name: 'actualLivingAddressPhoneTelephoneNo',
                value: actualLivingAddressPhone.telephoneNo,
              },
            ]
          : []),
        ...(workPhone
          ? [
              {
                name: 'workPhoneCountry',
                value: optionFormat(workPhone.countryCode),
              },
              {
                name: 'workPhoneFieldCode',
                value: workPhone.fieldCode,
              },
              {
                name: 'workPhoneTelephoneNo',
                value: workPhone.telephoneNo,
              },
            ]
          : []),
        ...(contactPersonMobilePhone1
          ? [
              {
                name: 'contactPersonMobilePhone1Country',
                value: optionFormat(contactPersonMobilePhone1.countryCode),
              },
              {
                name: 'contactPersonMobilePhone1FieldCode',
                value: contactPersonMobilePhone1.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone1TelephoneNo',
                value: contactPersonMobilePhone1.telephoneNo,
              },
            ]
          : []),

        ...(contactPersonMobilePhone2
          ? [
              {
                name: 'contactPersonMobilePhone2Country',
                value: optionFormat(contactPersonMobilePhone2.countryCode),
              },
              {
                name: 'contactPersonMobilePhone2FieldCode',
                value: contactPersonMobilePhone2.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone2TelephoneNo',
                value: contactPersonMobilePhone2.telephoneNo,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      // Acquire validations
      mobilePhone.countryCode &&
        handleCountryChange(
          'mobilePhone',
          PhoneType.MOBILEPHONE
        )(optionFormat(mobilePhone.countryCode));
      actualLivingAddressPhone?.countryCode &&
        handleCountryChange(
          'actualLivingAddressPhone',
          PhoneType.HOMEPHONE
        )(optionFormat(actualLivingAddressPhone.countryCode));
      workPhone?.countryCode &&
        handleCountryChange('workPhone', PhoneType.WORKPHONE)(optionFormat(workPhone.countryCode));
      contactPersonMobilePhone1?.countryCode &&
        handleCountryChange(
          'contactPersonMobilePhone1',
          PhoneType.CONTACTMOBILEPHONE
        )(optionFormat(contactPersonMobilePhone1.countryCode));
      contactPersonMobilePhone2?.countryCode &&
        handleCountryChange(
          'contactPersonMobilePhone2',
          PhoneType.CONTACTMOBILEPHONE
        )(optionFormat(contactPersonMobilePhone2.countryCode));
    } else {
      const { data: response } = await Customer.getCustomerContactInfo({
        customerId: customerId || '',
      });

      const mobilPhoneCountry = countryPhoneCodes.find(
        (country) => country.value === response.mobilePhone?.countryCode
      );
      const workPhoneCountry = countryPhoneCodes.find(
        (country) => country.value === response.workPhone?.countryCode
      );
      const actualLivingAddressPhoneCountry =
        countryPhoneCodes.find(
          (country) => country.value === response.actualLivingAddressPhone?.countryCode
        ) || null;
      const contactPersonMobilPhone1Country =
        countryPhoneCodes.find(
          (country) => country.value === response.contactPersonMobilePhone1?.countryCode
        ) || null;
      const contactPersonMobilPhone2Country =
        countryPhoneCodes.find(
          (country) => country.value === response.contactPersonMobilePhone2?.countryCode
        ) || null;
      [
        { name: 'emailAddress', value: response.emailAddress },
        { name: 'mobilePhoneCountry', value: mobilPhoneCountry },
        ...(response.mobilePhone?.fieldCode && response.mobilePhone?.telephoneNo
          ? [
              {
                name: 'mobilePhoneFieldCode',
                value: response.mobilePhone?.fieldCode,
              },
              {
                name: 'mobilePhoneTelephoneNo',
                value: response.mobilePhone?.telephoneNo,
              },
            ]
          : []),
        { name: 'actualLivingAddressPhoneCountry', value: actualLivingAddressPhoneCountry },
        ...(response.actualLivingAddressPhone?.fieldCode &&
        response.actualLivingAddressPhone?.telephoneNo
          ? [
              {
                name: 'actualLivingAddressPhoneFieldCode',
                value: response.actualLivingAddressPhone.fieldCode,
              },
              {
                name: 'actualLivingAddressPhoneTelephoneNo',
                value: response.actualLivingAddressPhone.telephoneNo,
              },
            ]
          : []),
        { name: 'workPhoneCountry', value: workPhoneCountry },
        ...(isCreditCard && response.workPhone?.fieldCode && response.workPhone?.telephoneNo
          ? [
              {
                name: 'workPhoneFieldCode',
                value: response.workPhone.fieldCode,
              },
              {
                name: 'workPhoneTelephoneNo',
                value: response.workPhone.telephoneNo,
              },
            ]
          : []),
        { name: 'contactPersonMobilePhone1Country', value: contactPersonMobilPhone1Country || '' },
        ...(response.contactPersonMobilePhone1?.fieldCode &&
        response.contactPersonMobilePhone1?.telephoneNo
          ? [
              {
                name: 'contactPersonMobilePhone1FieldCode',
                value: response.contactPersonMobilePhone1?.fieldCode || '',
              },
              {
                name: 'contactPersonMobilePhone1TelephoneNo',
                value: response.contactPersonMobilePhone1?.telephoneNo || '',
              },
            ]
          : []),

        ...(isCreditCard &&
        response.contactPersonMobilePhone2?.fieldCode &&
        response.contactPersonMobilePhone2?.telephoneNo
          ? [
              { name: 'contactPersonMobilePhone2Country', value: contactPersonMobilPhone2Country },
              {
                name: 'contactPersonMobilePhon2FieldCode',
                value: response.contactPersonMobilePhone2.fieldCode,
              },
              {
                name: 'contactPersonMobilePhone2TelephoneNo',
                value: response.contactPersonMobilePhone2.telephoneNo,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      // Acquire validations
      mobilPhoneCountry &&
        handleCountryChange('mobilePhone', PhoneType.MOBILEPHONE)(mobilPhoneCountry);
      actualLivingAddressPhoneCountry &&
        handleCountryChange(
          'actualLivingAddressPhone',
          PhoneType.HOMEPHONE
        )(actualLivingAddressPhoneCountry);
      isCreditCard &&
        workPhoneCountry &&
        handleCountryChange('workPhone', PhoneType.WORKPHONE)(workPhoneCountry);
      contactPersonMobilPhone1Country &&
        handleCountryChange(
          'contactPersonMobilePhone1',
          PhoneType.CONTACTMOBILEPHONE
        )(contactPersonMobilPhone1Country);
      isCreditCard &&
        contactPersonMobilPhone2Country &&
        handleCountryChange(
          'contactPersonMobilePhone2',
          PhoneType.CONTACTMOBILEPHONE
        )(contactPersonMobilPhone2Country);
    }
    setIsLoading(false);
  }, [countryPhoneCodes, customerId, isCreditCard, setValue]);

  useEffect(() => {
    if (countryPhoneCodes.length) {
      loadPhoneEmailInformation();
    }
  }, [countryPhoneCodes.length, loadPhoneEmailInformation]);

  const handleCountryChange = (
    fieldName: string,
    phoneType: PhoneType,
    onChange?: (option: Option | null) => void
  ) => async (country: Option | null) => {
    onChange && onChange(country);
    if (country) {
      const { data } = await CustomerContactInfo.getCustomerContactInfoFormat({
        phoneType,
        countryCode: country?.value || '',
      });
      setFormValidations((state) => ({ ...state, [fieldName]: data }));
    } else {
      setFormValidations(undefined);
    }
  };

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await ContactInfoPageValidation.validateContactInfoPageData({
      emailAddress: form.emailAddress,
      mobilePhone: {
        countryCode: dictionaryLineFormat(form.mobilePhoneCountry),
        fieldCode: form.mobilePhoneFieldCode,
        telephoneNo: form.mobilePhoneTelephoneNo,
      },
      ...(form.actualLivingAddressPhoneCountry &&
      form.actualLivingAddressPhoneFieldCode &&
      form.actualLivingAddressPhoneTelephoneNo
        ? {
            actualLivingAddressPhone: {
              countryCode: dictionaryLineFormat(form.actualLivingAddressPhoneCountry),
              fieldCode: form.actualLivingAddressPhoneFieldCode,
              telephoneNo: form.actualLivingAddressPhoneTelephoneNo,
            },
          }
        : {}),
      ...(form.workPhoneCountry
        ? {
            workPhone: {
              countryCode: dictionaryLineFormat(form.workPhoneCountry),
              fieldCode: form.workPhoneFieldCode,
              telephoneNo: form.workPhoneTelephoneNo,
            },
          }
        : {}),
      ...(form.contactPersonMobilePhone1Country &&
      form.contactPersonMobilePhone1FieldCode &&
      form.contactPersonMobilePhone1TelephoneNo
        ? {
            contactPersonMobilePhone1: {
              countryCode: dictionaryLineFormat(form.contactPersonMobilePhone1Country),
              fieldCode: form.contactPersonMobilePhone1FieldCode,
              telephoneNo: form.contactPersonMobilePhone1TelephoneNo,
            },
          }
        : {}),
      ...(form.contactPersonMobilePhone2Country &&
      form.contactPersonMobilePhone2FieldCode &&
      form.contactPersonMobilePhone2TelephoneNo
        ? {
            contactPersonMobilePhone2: {
              countryCode: dictionaryLineFormat(form.contactPersonMobilePhone2Country),
              fieldCode: form.contactPersonMobilePhone2FieldCode,
              telephoneNo: form.contactPersonMobilePhone2TelephoneNo,
            },
          }
        : {}),
    });

    if (data.result === 'OK') {
      await searchBankrupt(SearchBankruptInterfaceType.CARDENTRY);
      if (customerId) {
        await CustomerOperations.updateCustomer();
        goToNextStep(customerId);
      } else {
        const { data: response } = await CustomerOperations.createCustomer();
        if (response.result === 'OK') {
          switchCustomerTypeDispatch({
            isNewCustomer: false,
            customerId: response.customerId,
          });
          goToNextStep(response.customerId);
        }
      }
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_phoneEmailEntry_phoneEmailInformation_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_phoneEmailEntry_email_subtitle" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="emailAddress"
              render={({ onChange, value }) => (
                <TextInput
                  disabled={disablePage}
                  errorMessage={t(errors?.emailAddress?.message as string)}
                  formatter={formatEmail}
                  label={t('cardEntry_phoneEmailEntry_email_label')}
                  name="emailAddress"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_phoneEmailEntry_mobilePhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="mobilePhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={!isNewCustomer || disablePage}
                  errorMessage={t((errors?.mobilePhoneCountry as FieldError)?.message as string)}
                  items={countryPhoneCodes}
                  label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                  name="mobilePhoneCountry"
                  onChange={handleCountryChange('mobilePhone', PhoneType.MOBILEPHONE, onChange)}
                  searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="mobilePhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!isNewCustomer || !watch('mobilePhoneCountry') || disablePage}
                    errorMessage={t(errors?.mobilePhoneFieldCode?.message as string, {
                      min: formValidations?.mobilePhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.mobilePhone?.maxFieldCodeLength}
                    name="mobilePhoneFieldCode"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="mobilePhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!isNewCustomer || !watch('mobilePhoneCountry') || disablePage}
                    errorMessage={t(errors?.mobilePhoneTelephoneNo?.message as string, {
                      min: formValidations?.mobilePhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.mobilePhone?.maxTelephoneNoLength}
                    name="mobilePhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        {isCreditCard ? (
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="cardEntry_phoneEmailEntry_workPhone_subtitle" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="workPhoneCountry"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t((errors?.workPhoneCountry as FieldError)?.message as string)}
                      items={countryPhoneCodes}
                      label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                      name="workPhoneCountry"
                      onChange={handleCountryChange('workPhone', PhoneType.WORKPHONE, onChange)}
                      searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                      value={value}
                      required
                      searchable
                    />
                  )}
                />
              </FormElement>
              <FlexFormElement>
                <AreaCode>
                  <Controller
                    control={control}
                    name="workPhoneFieldCode"
                    render={({ onChange, value }) => (
                      <TextInput
                        disabled={!watch('workPhoneCountry') || disablePage}
                        errorMessage={t(errors.workPhoneFieldCode?.message as string, {
                          min: formValidations?.workPhone?.minFieldCodeLength,
                        })}
                        formatter={formatNumeric}
                        label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                        maxLength={formValidations?.workPhone?.maxFieldCodeLength}
                        name="workPhoneFieldCode"
                        onChange={onChange}
                        value={value}
                        required
                      />
                    )}
                  />
                </AreaCode>
                <PhoneNumber>
                  <Controller
                    control={control}
                    name="workPhoneTelephoneNo"
                    render={({ onChange, value }) => (
                      <TextInput
                        disabled={!watch('workPhoneCountry') || disablePage}
                        errorMessage={t(errors.workPhoneTelephoneNo?.message as string, {
                          min: formValidations?.workPhone?.minTelephoneNoLength,
                        })}
                        formatter={formatNumeric}
                        label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                        maxLength={formValidations?.workPhone?.maxTelephoneNoLength}
                        name="workPhoneTelephoneNo"
                        onChange={onChange}
                        value={value}
                        required
                      />
                    )}
                  />
                </PhoneNumber>
              </FlexFormElement>
            </FormRow>
          </>
        ) : null}
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_phoneEmailEntry_ContactPersonPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="contactPersonMobilePhone1Country"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t(
                    (errors?.contactPersonMobilePhone1Country as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                  name="contactPersonMobilePhone1Country"
                  onChange={handleCountryChange(
                    'contactPersonMobilePhone1',
                    PhoneType.CONTACTMOBILEPHONE,
                    onChange
                  )}
                  required={isCreditCard}
                  searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="contactPersonMobilePhone1FieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone1Country') || disablePage}
                    errorMessage={t(errors.contactPersonMobilePhone1FieldCode?.message as string, {
                      min: formValidations?.contactPersonMobilePhone1?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.contactPersonMobilePhone1?.maxFieldCodeLength}
                    name="contactPersonMobilePhone1FieldCode"
                    onChange={onChange}
                    required={isCreditCard}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="contactPersonMobilePhone1TelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('contactPersonMobilePhone1Country') || disablePage}
                    errorMessage={t(
                      errors.contactPersonMobilePhone1TelephoneNo?.message as string,
                      {
                        min: formValidations?.contactPersonMobilePhone1?.minTelephoneNoLength,
                      }
                    )}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.contactPersonMobilePhone1?.maxTelephoneNoLength}
                    name="contactPersonMobilePhone1TelephoneNo"
                    onChange={onChange}
                    required={isCreditCard}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        {isCreditCard ? (
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="cardEntry_phoneEmailEntry_ContactPersonPhone_subtitle" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="contactPersonMobilePhone2Country"
                  render={({ onChange, value }) => (
                    <Combobox
                      disabled={disablePage}
                      errorMessage={t(
                        (errors?.contactPersonMobilePhone2Country as FieldError)?.message as string
                      )}
                      items={countryPhoneCodes}
                      label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                      name="contactPersonMobilePhone2Country"
                      onChange={handleCountryChange(
                        'contactPersonMobilePhone2',
                        PhoneType.CONTACTMOBILEPHONE,
                        onChange
                      )}
                      searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                      value={value}
                      searchable
                    />
                  )}
                />
              </FormElement>
              <FlexFormElement>
                <AreaCode>
                  <Controller
                    control={control}
                    name="contactPersonMobilePhone2FieldCode"
                    render={({ onChange, value }) => (
                      <TextInput
                        disabled={!watch('contactPersonMobilePhone2Country') || disablePage}
                        errorMessage={t(
                          errors.contactPersonMobilePhone2FieldCode?.message as string,
                          {
                            min: formValidations?.contactPersonMobilePhone2?.minFieldCodeLength,
                          }
                        )}
                        formatter={formatNumeric}
                        label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                        maxLength={formValidations?.contactPersonMobilePhone2?.maxFieldCodeLength}
                        name="contactPersonMobilePhone2FieldCode"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </AreaCode>
                <PhoneNumber>
                  <Controller
                    control={control}
                    name="contactPersonMobilePhone2TelephoneNo"
                    render={({ onChange, value }) => (
                      <TextInput
                        disabled={!watch('contactPersonMobilePhone2Country') || disablePage}
                        errorMessage={t(
                          errors.contactPersonMobilePhone2TelephoneNo?.message as string,
                          {
                            min: formValidations?.contactPersonMobilePhone2?.minTelephoneNoLength,
                          }
                        )}
                        formatter={formatNumeric}
                        label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                        maxLength={formValidations?.contactPersonMobilePhone2?.maxTelephoneNoLength}
                        name="contactPersonMobilePhone2TelephoneNo"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </PhoneNumber>
              </FlexFormElement>
            </FormRow>
          </>
        ) : null}
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_phoneEmailEntry_livingAddressPhone_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="actualLivingAddressPhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t(
                    (errors?.actualLivingAddressPhoneCountry as FieldError)?.message as string
                  )}
                  items={countryPhoneCodes}
                  label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                  name="actualLivingAddressPhoneCountry"
                  onChange={handleCountryChange(
                    'actualLivingAddressPhone',
                    PhoneType.HOMEPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="actualLivingAddressPhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('actualLivingAddressPhoneCountry') || disablePage}
                    errorMessage={t(errors.actualLivingAddressPhoneFieldCode?.message as string, {
                      min: formValidations?.actualLivingAddressPhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.actualLivingAddressPhone?.maxFieldCodeLength}
                    name="actualLivingAddressPhoneFieldCode"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="actualLivingAddressPhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('actualLivingAddressPhoneCountry') || disablePage}
                    errorMessage={t(errors.actualLivingAddressPhoneTelephoneNo?.message as string, {
                      min: formValidations?.actualLivingAddressPhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.actualLivingAddressPhone?.maxTelephoneNoLength}
                    name="actualLivingAddressPhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>

        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default PhoneEmailEntry;
